import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Paper,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import {
  fetchDepartements,
  fetchAllPrixDepartement,
  createOrUpdatePrixDepartement,
  deletePrixDepartement,
} from '../../reduxStore/DepartementSlice';
import { Article, ArticlePrixDepartement } from '../../models/models';
import { useSnackbar } from '../SnackbarProvider';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSION_PRIX_DEPT, PERMISSION_PRIX_REVIENT } from '../../constants/enums';

interface Props {
  article: Article;
  /** Appelé après save/delete d'un override pour permettre au parent de
   *  rafraîchir une liste qui dépend des prix effectifs (ex: catalogue). */
  onSaved?: () => void;
}

const ArticlePrixDepartementForm: React.FC<Props> = ({ article, onSaved }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { departements, prixDepartement, loading } = useSelector(
    (state: RootState) => state.DepartementSlice
  );
  const selectedDepartement = useSelector((state: RootState) => state.AppSlice.selectedDepartement);
  // Droits fins (0154) : sans « modifier », champs grisés ; sans « supprimer », pas de retour au prix de base
  const peutModifier  = usePermission(PERMISSION_PRIX_DEPT.UPDATE);
  const peutSupprimer = usePermission(PERMISSION_PRIX_DEPT.DELETE);
  // Sans le droit de voir le coût (0156) : colonne prix de revient absente (l'API garde la valeur)
  const voitCout      = usePermission(PERMISSION_PRIX_REVIENT);

  // En mode dept : restreindre l'affichage au dept courant uniquement.
  // En mode ALL : tous les départements.
  const displayedDepartements = selectedDepartement
    ? departements.filter((d) => d.id === selectedDepartement.id)
    : departements;

  // Bases de prix pour les nouvelles overrides : utilise les défauts bruts si
  // disponibles (cf. migration 0026), sinon retombe sur les effectifs.
  const basePrixV  = article.prix_v_defaut  ?? article.prix_v;
  const basePrixR  = article.prix_r_defaut  ?? article.prix_r;
  const baseRemise = article.remise_defaut  ?? article.remise;

  // état local : une ligne de saisie par département
  type RowState = { prix_v: string; remise: string; prix_r: string; id?: number };
  const [rows, setRows] = useState<Record<number, RowState>>({});
  // Snapshot initial pour détecter les modifications utilisateur — bouton
  // Enregistrer affiché uniquement si la ligne dévie de son état d'origine.
  const [initialRows, setInitialRows] = useState<Record<number, RowState>>({});

  useEffect(() => {
    if (departements.length === 0) dispatch(fetchDepartements());
  }, [dispatch, departements.length]);

  useEffect(() => {
    if (article.id) {
      dispatch(fetchAllPrixDepartement({ article_id: article.id }));
    }
  }, [dispatch, article.id]);

  // Quand les prix chargent, initialiser les lignes
  useEffect(() => {
    const map: Record<number, RowState> = {};

    departements.forEach((d) => {
      const existing = prixDepartement.find(
        (p) => p.departement_id === d.id && p.article_id === article.id
      );
      map[d.id!] = {
        id:     existing?.id,
        prix_v: existing ? String(existing.prix_v) : String(basePrixV),
        remise: existing ? String(existing.remise) : String(baseRemise),
        prix_r: existing ? String(existing.prix_r) : String(basePrixR),
      };
    });

    setRows(map);
    setInitialRows(map);
  }, [departements, prixDepartement, article, basePrixV, basePrixR, baseRemise]);

  const isTouched = (deptId: number): boolean => {
    const cur  = rows[deptId];
    const init = initialRows[deptId];
    if (!cur || !init) return false;
    return cur.prix_v !== init.prix_v
        || cur.prix_r !== init.prix_r
        || cur.remise !== init.remise;
  };

  const handleChange = (deptId: number, field: 'prix_v' | 'remise' | 'prix_r', value: string) => {
    setRows((prev) => ({
      ...prev,
      [deptId]: { ...prev[deptId], [field]: value },
    }));
  };

  const handleSave = async (deptId: number, deptNom: string) => {
    const row = rows[deptId];
    const payload: ArticlePrixDepartement = {
      id:                   row.id,
      article_id:           article.id!,
      departement_id:       deptId,   // utilisé localement pour le state Redux
      prix_departement_id:  deptId,   // lu par le backend à la place de departement_id
      prix_v:               parseFloat(row.prix_v) || 0,
      remise:               parseFloat(row.remise) || 0,
      prix_r:               parseFloat(row.prix_r) || 0,
    };
    try {
      await dispatch(createOrUpdatePrixDepartement(payload)).unwrap();
      showSnackbar(`Prix enregistré pour ${deptNom}`, 'success');
      // recharger pour récupérer l'id si création
      dispatch(fetchAllPrixDepartement({ article_id: article.id }));
      onSaved?.();
    } catch {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (deptId: number, deptNom: string) => {
    const row = rows[deptId];
    if (!row?.id) return; // pas encore sauvegardé
    try {
      await dispatch(deletePrixDepartement(row.id)).unwrap();
      showSnackbar(`Prix supprimé pour ${deptNom}, revient au prix de base`, 'info');
      setRows((prev) => ({
        ...prev,
        [deptId]: { prix_v: String(basePrixV), remise: String(baseRemise), prix_r: String(basePrixR) },
      }));
      onSaved?.();
    } catch {
      showSnackbar('Erreur lors de la suppression', 'error');
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Article : <strong>{article.code}</strong> — {article.designation}
      </Typography>
      {!selectedDepartement && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Prix de base : <strong>{basePrixV}</strong>
          {voitCout && <> | Prix revient base : <strong>{basePrixR}</strong></>}
          {' '}| Remise base : <strong>{baseRemise}%</strong>
          <br />
          Laissez les valeurs identiques au prix de base pour utiliser le prix global.
        </Typography>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Département</strong></TableCell>
              {voitCout && <TableCell align="right"><strong>Prix revient (DA)</strong></TableCell>}
              <TableCell align="right"><strong>Prix vente (DA)</strong></TableCell>
              <TableCell align="right"><strong>Remise (%)</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedDepartements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={voitCout ? 5 : 4} align="center">
                  Aucun département configuré
                </TableCell>
              </TableRow>
            ) : (
              displayedDepartements.map((dept) => {
                const row = rows[dept.id!] ?? { prix_v: String(basePrixV), remise: String(baseRemise), prix_r: String(basePrixR) };
                const isSaved = !!row.id;
                return (
                  <TableRow key={dept.id} hover>
                    <TableCell>{dept.nom}</TableCell>

                    {voitCout && (
                      <TableCell align="right" sx={{ width: 140 }}>
                        <TextField
                          value={row.prix_r}
                          onChange={(e) => handleChange(dept.id!, 'prix_r', e.target.value)}
                          disabled={!peutModifier}
                          type="number"
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                    )}

                    <TableCell align="right" sx={{ width: 140 }}>
                      <TextField
                        value={row.prix_v}
                        onChange={(e) => handleChange(dept.id!, 'prix_v', e.target.value)}
                        disabled={!peutModifier}
                        type="number"
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    
                    <TableCell align="right" sx={{ width: 120 }}>
                      <TextField
                        value={row.remise}
                        onChange={(e) => handleChange(dept.id!, 'remise', e.target.value)}
                        disabled={!peutModifier}
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      {peutModifier && isTouched(dept.id!) && (
                        <Tooltip title="Enregistrer">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSave(dept.id!, dept.nom)}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {peutSupprimer && isSaved && (
                        <Tooltip title="Supprimer (revient au prix de base)">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(dept.id!, dept.nom)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default ArticlePrixDepartementForm;
